import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import Footer from "@/components/Footer";
import { generatePageTitle, generateCanonicalUrl } from "@/lib/seoConfig";

const AccessibilityStatement = () => {
  return (
    <>
      <Helmet>
        <title>{generatePageTitle("Accessibility Statement")}</title>
        <meta name="description" content="iQ Texting's commitment to digital accessibility. Learn about our WCAG 2.1 compliance and accessibility features for users with disabilities." />
        <link rel="canonical" href={generateCanonicalUrl("/accessibility-statement")} />
        <meta property="og:title" content={generatePageTitle("Accessibility Statement")} />
        <meta property="og:description" content="Our commitment to making iQ Texting accessible to everyone." />
        <meta property="og:url" content={generateCanonicalUrl("/accessibility-statement")} />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-6">
          <Button variant="ghost" asChild className="mb-4">
            <Link to="/">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Link>
          </Button>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
            Accessibility Statement
          </h1>
          <p className="text-muted-foreground mt-2">Our commitment to accessibility</p>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="prose prose-slate dark:prose-invert max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-semibold mb-4">Our Commitment</h2>
            <p className="text-muted-foreground">
              iQ Texting is committed to ensuring digital accessibility for people with disabilities. We are 
              continually improving the user experience for everyone and applying the relevant accessibility standards.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Conformance Status</h2>
            <p className="text-muted-foreground">
              We aim to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA standards. 
              These guidelines explain how to make web content more accessible for people with disabilities.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Accessibility Features</h2>
            <p className="text-muted-foreground mb-2">Our website includes the following accessibility features:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Keyboard navigation support</li>
              <li>Screen reader compatibility</li>
              <li>Alternative text for images</li>
              <li>Clear and consistent navigation</li>
              <li>Sufficient color contrast</li>
              <li>Resizable text without loss of functionality</li>
              <li>Clear focus indicators for interactive elements</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Technical Specifications</h2>
            <p className="text-muted-foreground">
              Accessibility of iQ Texting relies on the following technologies to work with the particular 
              combination of web browser and any assistive technologies or plugins installed on your computer:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-2">
              <li>HTML</li>
              <li>WAI-ARIA</li>
              <li>CSS</li>
              <li>JavaScript</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Limitations and Alternatives</h2>
            <p className="text-muted-foreground">
              Despite our best efforts, some content may not yet be fully accessible. We are actively working to 
              increase the accessibility and usability of our website and in doing so adhere to available standards 
              and guidelines.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Feedback</h2>
            <p className="text-muted-foreground">
              We welcome your feedback on the accessibility of iQ Texting. Please let us know if you encounter 
              accessibility barriers:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-2">
              <li>Email: accessibility@iqtexting.com</li>
              <li>Phone: 1-800-IQ-TEXTS</li>
            </ul>
            <p className="text-muted-foreground mt-4">
              We try to respond to feedback within 2 business days.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Ongoing Efforts</h2>
            <p className="text-muted-foreground">
              We regularly review our website and conduct accessibility audits to identify and fix issues. 
              Our team receives ongoing training on accessibility best practices to ensure we continue to 
              improve the accessibility of our services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Date</h2>
            <p className="text-muted-foreground">
              This statement was created on March 2025 and last reviewed on March 2025.
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
    </>
  );
};

export default AccessibilityStatement;
